<section class="title-section">
  <h2 class="incidents-title">Incidents Hub</h2>
  @if (this.isAdminUser) {
    <button class="button-secondary" type="button" mat-flat-button (click)="addIncidentClickedAsync()">
      Add incident
    </button>
  }
</section>

<section class="section-box filters">
  <form [formGroup]="formGroup" class="container-full-width">
    <!-- Top row always expanded -->
    <div class="filters-container">
      <!-- Left container -->
      <div class="filters-container-inside">
        <mat-form-field appearance="outline" floatLabel="always" class="container-full-width">
          <mat-label>Search by</mat-label>
          <input
            matInput
            formControlName="searchBy"
            placeholder="Type to search - use AND, OR to combine terms"
            [value]="filters.searchBy.value"
          />
          <mat-hint>You can use “AND”, “OR” to combine terms</mat-hint>
        </mat-form-field>
      </div>

      <!-- Middle container -->
      <div class="filters-container-inside">
        <mat-form-field appearance="outline" floatLabel="always" class="container-full-width">
          <mat-label>Search in</mat-label>
          <mat-select formControlName="searchIn" multiple placeholder="Pick one or more">
            @for (option of searchInOptions; track option) {
              <mat-option [value]="option.value">
                {{ option.label }}
              </mat-option>
            }
          </mat-select>
          @if (formGroup.get('searchIn')?.hasError('invalidSearchMode')) {
            <mat-error>Please select at least one search mode.</mat-error>
          }
          <mat-hint>This will define the search scope</mat-hint>
        </mat-form-field>
      </div>

      <!-- Right container -->
      <div class="filters-input-dates">
        <mat-form-field appearance="outline" floatLabel="always">
          <mat-label>Select start date</mat-label>
          <input
            matInput
            [matDatepicker]="picker1"
            (focus)="picker1.open()"
            formControlName="dateStart"
            placeholder="Input"
            [readonly]="true"
          />
          <mat-datepicker-toggle matPrefix [for]="picker1"></mat-datepicker-toggle>
          <mat-datepicker #picker1></mat-datepicker>
          @if (formGroup | filterHasValue: 'dateStart') {
            <button
              class="filter-clear-btn"
              mat-icon-button
              matSuffix
              aria-label="Clear selection"
              (click)="clearIndividualFilter($event, 'dateStart')"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>

        <a class="date-separator">to</a>

        <mat-form-field appearance="outline" floatLabel="always">
          <mat-label>Select end date</mat-label>
          <input
            matInput
            [matDatepicker]="picker2"
            (focus)="picker2.open()"
            formControlName="dateEnd"
            placeholder="Input"
            [readonly]="true"
          />
          <mat-datepicker-toggle matPrefix [for]="picker2"></mat-datepicker-toggle>
          <mat-datepicker #picker2></mat-datepicker>
          @if (formGroup | filterHasValue: 'dateEnd') {
            <button
              class="filter-clear-btn"
              mat-icon-button
              matSuffix
              aria-label="Clear selection"
              (click)="clearIndividualFilter($event, 'dateEnd')"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
      </div>
    </div>

    <!-- Expandable area -->
    @if (filtersExpanded) {
      <div class="filters-container filters-container-top-margin">
        <!-- Left container -->
        <div class="filters-container-inside-two-columns">
          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Actor Type</mat-label>
            <mat-select formControlName="actorTypes" multiple placeholder="Pick one or more">
              @for (option of actorTypeOptions; track option) {
                <mat-option [value]="option.value">
                  {{ option.label }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (this.isAdminUser) {
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>Visibility</mat-label>
              <mat-select formControlName="incidentDisabled" placeholder="Pick one">
                <mat-option value="all">All visible</mat-option>
                <mat-option value="false">Active</mat-option>
                <mat-option value="true">Disabled</mat-option>
              </mat-select>
            </mat-form-field>
          }

          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Entity Size</mat-label>
            <mat-select formControlName="entitySize" multiple placeholder="Pick one or more">
              @for (option of entitySizeOptions; track option) {
                <mat-option [value]="option.value" [matTooltip]="entityTooltipDescriptor[option.value]">
                  {{ option.label }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Geo-Location</mat-label>
            <mat-select
              [formControl]="countryCodesControl"
              multiple
              placeholder="Pick one or more"
              (selectionChange)="onCountrySelectionChange($event)"
            >
              @for (continent of continents; track continent) {
                <mat-optgroup class="tree-selectable" [label]="continent.label" #matOptgroupLabel>
                  <div class="continent-header">
                    <mat-checkbox
                      disableRipple
                      #continentCheckbox
                      [checked]="isContinentFullySelected(continent)"
                      [indeterminate]="isContinentIndeterminate(continent)"
                      (click)="onContinentCheckboxChanged(continent, continentCheckbox)"
                    />
                  </div>
                  @for (country of continent.countries; track country) {
                    <mat-option class="tree-selectable" [value]="continent.name + ':' + country">
                      {{ countryDescriptor[country] }}
                    </mat-option>
                  }
                </mat-optgroup>
              }
            </mat-select>
          </mat-form-field>

          @if (this.isAdminUser) {
            <mat-form-field appearance="outline" floatLabel="always">
              <mat-label>Incident Type</mat-label>
              <mat-select formControlName="incidentTypes" multiple placeholder="Pick one or more">
                @for (option of incidentTypeOptions; track option) {
                  <mat-option [value]="option.value">
                    {{ option.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }

          <mat-form-field appearance="outline" floatLabel="always" class="container-full-width">
            <mat-label>Dun & Bradstreet Number</mat-label>
            <input
              matInput
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              formControlName="dnbId"
              placeholder="Input"
              [value]="filters.dnbId.value"
              (keydown)="blockInvalidNumericSymbol($event, true)"
              (input)="onDnbIdInput($event)"
            />
          </mat-form-field>
        </div>

        <!-- Middle container -->
        <div class="filters-container-inside">
          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Revenue From</mat-label>
            <input
              matInput
              formControlName="revenueFrom"
              type="number"
              placeholder="Input"
              (keydown)="blockInvalidNumericSymbol($event)"
              [value]="filters.revenueFrom.value"
            />
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Motivation</mat-label>
            <mat-select formControlName="motivation" multiple placeholder="Pick one or more">
              @for (option of motivationOptions; track option) {
                <mat-option [value]="option.value">
                  {{ option.label }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>

          <div class="slide-toggle-container">
            <mat-slide-toggle
              formControlName="includeSubsidiaries"
              class="slide-toggle"
              [checked]="filters.includeSubsidiaries.value"
            >
            </mat-slide-toggle>
            Include Subsidiaries
          </div>
        </div>

        <!-- Right container -->
        <div class="filters-container-inside">
          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Revenue To</mat-label>
            <input
              matInput
              formControlName="revenueTo"
              type="number"
              placeholder="Input"
              (keydown)="blockInvalidNumericSymbol($event)"
              [value]="filters.revenueTo.value"
            />
          </mat-form-field>

          <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>Business Sector</mat-label>
            <mat-select formControlName="businessSector" multiple placeholder="Pick one or more">
              @for (option of businessSectorOptions; track option) {
                <mat-option [value]="option.value">
                  {{ option.label }}
                </mat-option>
              }
            </mat-select>
            @if (formGroup | filterHasValue: 'businessSector') {
              <button
                class="filter-clear-btn"
                mat-icon-button
                matSuffix
                aria-label="Clear selection"
                (click)="clearIndividualFilter($event, 'businessSector')"
              >
                <mat-icon>close</mat-icon>
              </button>
            }
          </mat-form-field>
        </div>
      </div>
    }

    <!-- Action buttons -->
    <div class="filters-container-action-buttons">
      <div>
        @if (filtersExpanded) {
          <button
            class="button-link button-expand"
            type="button"
            mat-flat-button
            (click)="filtersExpanded = !filtersExpanded"
          >
            <div>Less filters</div>
            <mat-icon class="button-expand-icon">expand_less</mat-icon>
          </button>
        } @else {
          <button
            class="button-link button-expand"
            type="button"
            mat-flat-button
            (click)="filtersExpanded = !filtersExpanded"
          >
            <div>More filters</div>
            <mat-icon class="button-expand-icon">expand_more</mat-icon>
          </button>
        }
      </div>

      <div class="form-actions-buttons">
        <button class="button-link" type="button" mat-flat-button (click)="onResetAsync()">Clear</button>
        <button
          class="button-primary"
          type="submit"
          mat-flat-button
          (click)="onSearchAsync()"
          [disabled]="formGroup.invalid"
        >
          Search
        </button>
      </div>
    </div>
  </form>
</section>

<section class="section-box incidents-table">
  <table
    mat-table
    matSort
    [dataSource]="dataSource"
    class="mat-elevation-z8 custom-table"
    (matSortChange)="onSortChangeAsync($event)"
  >
    <!-- Title Column -->
    <ng-container matColumnDef="title">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Title</th>
      <!-- This is an accessability trick. Inside the mat-cell we should put an anchor tag with this class.
                  The anchor fills all the space and making the right-click on the row show the correct link options (like open in a new tab).
                  Check the css classes-->
      <td class="mat-cell-link" mat-cell *matCellDef="let element">
        <a class="mat-cell-anchor-link" [routerLink]="element.id"> {{ element.title }}</a>
      </td>
    </ng-container>

    <!-- Entity Column -->
    <ng-container matColumnDef="entity">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Entity</th>
      <td class="mat-cell-link" mat-cell *matCellDef="let element">
        <a class="mat-cell-anchor-link" [routerLink]="element.id"> {{ element.entity ?? 'No DnB-Entity found' }}</a>
      </td>
    </ng-container>

    <!-- IncidentDate Column -->
    <ng-container matColumnDef="incidentDate">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Incident Date</th>
      <td class="mat-cell-link" mat-cell *matCellDef="let element">
        <a class="mat-cell-anchor-link" [routerLink]="element.id"> {{ element.incidentDate | appDate }}</a>
      </td>
    </ng-container>

    <!-- Status Column -->
    <ng-container matColumnDef="status">
      <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
      <td class="mat-cell-link" mat-cell *matCellDef="let element">
        <a class="mat-cell-anchor-link" [routerLink]="element.id">
          @if (!element.isReviewed) {
            <div class="status-flag"><mat-icon>person_search</mat-icon><span>Under review</span></div>
          }
        </a>
      </td>
    </ng-container>

    <!-- Type Column -->
    <ng-container matColumnDef="type">
      <th mat-header-cell *matHeaderCellDef>Type</th>
      <td class="mat-cell-link" mat-cell *matCellDef="let element">
        <a class="mat-cell-anchor-link" [routerLink]="element.id">
          @if (element.isMasterIncident) {
            <div class="type-flag type-flag-merged"><span>Merged</span></div>
          }
          @if (element.masterIncidentId !== null) {
            <div
              class="type-flag type-flag-duplicate"
              [matTooltip]="'Duplicate of incident ' + element.masterIncidentId"
              matTooltipPosition="below"
            >
              <span>Duplicate</span>
            </div>
          }
        </a>
      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="incidentsTableColumns"></tr>
    <tr
      [ngClass]="{ 'table-row': true, 'disabled-row': row.status === IncidentStatus.Disabled }"
      mat-row
      *matRowDef="let row; columns: incidentsTableColumns"
    ></tr>
  </table>

  @if (filteredIncidents.length > 0) {
    <mat-paginator
      [pageSizeOptions]="[10, 25, 50, 100]"
      [length]="totalEntries"
      [pageSize]="pageSize"
      [pageIndex]="pageNumber - 1"
      (page)="onPageChangeAsync($event)"
    >
    </mat-paginator>
  }

  @if (!filteredIncidents.length) {
    <p>No incidents found.</p>
  }
</section>
